import React, { useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAP_BOX_KEY } from '../../config/config';

interface MapboxMapProps {
  latitude?: number;
  longitude?: number;
  onMarkerDragEnd?: (latitude: number, longitude: number) => void;
}

const MapboxMap: React.FC<MapboxMapProps> = ({
  latitude = -30.5595,
  longitude = 22.9375,
  onMarkerDragEnd,
}) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  // Initialize map only once
  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapboxgl.accessToken = MAP_BOX_KEY || '';
    
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [longitude, latitude],
      zoom: latitude === -30.5595 ? 5 : 12
    });

    // Create marker
    markerRef.current = new mapboxgl.Marker({
      color: '#BBAA64',
      draggable: true
    })
      .setLngLat([longitude, latitude])
      .addTo(mapRef.current);

    // Add drag end event listener
    if (onMarkerDragEnd) {
      markerRef.current.on('dragend', () => {
        const lngLat = markerRef.current?.getLngLat();
        if (lngLat) {
          onMarkerDragEnd(lngLat.lat, lngLat.lng);
        }
      });
    }

    // Cleanup
    return () => {
      markerRef.current?.remove();
      mapRef.current?.remove();
    };
  }, []); // Empty dependency array - only run once

  // Update marker position when coordinates change
  useEffect(() => {
    if (markerRef.current && mapRef.current) {
      markerRef.current.setLngLat([longitude, latitude]);
      mapRef.current.flyTo({
        center: [longitude, latitude],
        zoom: latitude === -30.5595 ? 5 : 12,
        essential: true
      });
    }
  }, [latitude, longitude]);

  return (
    <div className='mb-4'
      ref={mapContainerRef} 
      style={{ width: '100%', height: '400px' }}
    />
  );
};

export default MapboxMap;